import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { entityLink } from "../lib/drill";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const KPI_DEFS = [
  ["total_requisitions", "Requisitions", "/requisitions"],
  ["open_requisitions", "Open Roles", "/requisitions?status=open"],
  ["pending_approvals", "Pending Approvals", "/requisitions?status=pending_approval"],
  ["total_candidates", "Candidates", "/candidates"],
  ["interviews_scheduled", "Interviews", "/interviews"],
  ["offers_in_flight", "Offers In Flight", "/offers"],
  ["hired", "Hired", "/candidates?stage=hired"],
  ["ai_screened", "AI Screened", "/candidates"],
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [narrative, setNarrative] = useState("");
  const [genBusy, setGenBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/analytics/dashboard").then((r) => setData(r.data)).catch(() => toast.error("Failed to load dashboard"));
  }, []);

  const genNarrative = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post("/analytics/ai-narrative");
      setNarrative(data.narrative);
    } catch (e) {
      toast.error("AI narrative failed");
    } finally {
      setGenBusy(false);
    }
  };

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#E05A47] font-bold">Executive Overview</div>
          <h1 className="font-heading font-black text-3xl tracking-tight mt-1">Recruitment Command Center</h1>
        </div>
        <button data-testid="generate-ai-briefing-button" onClick={genNarrative} disabled={genBusy}
          className="flex items-center gap-2 bg-[#E05A47] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#C74936] transition-colors duration-150 disabled:opacity-60">
          <Sparkles size={14} /> {genBusy ? "Generating…" : "AI Executive Briefing"}
        </button>
      </div>

      {narrative && (
        <div data-testid="ai-narrative-panel" className="mt-5 border-l-4 border-[#0A251D] bg-white border p-5 text-sm leading-relaxed whitespace-pre-line">
          {narrative}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 rise-in-1">
        {KPI_DEFS.map(([key, label, to]) => (
          <Link to={to} key={key} data-testid={`kpi-${key}`}
            className="kpi-card border bg-white rounded-xl p-5 block">
            <div className="font-heading font-black text-3xl tracking-tight text-[#0A251D]">{data.kpis[key]}</div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 border bg-white rounded-xl p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1 font-bold">Hiring Funnel</div>
          <div className="text-[11px] text-muted-foreground mb-3">Click a bar to drill into that stage</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.funnel}>
              <XAxis dataKey="stage" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
              <Tooltip cursor={{ fill: "rgba(10,37,29,0.06)" }} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} cursor="pointer"
                onClick={(bar) => { const s = bar?.payload?.stage || bar?.stage; if (s) navigate(`/candidates?stage=${s}`); }}>
                {data.funnel.map((_, i) => <Cell key={i} fill={i % 2 ? "#3E6F5C" : "#0A251D"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="border bg-white rounded-xl p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3 font-bold">Candidates by Source</div>
          {data.sources.map((s) => (
            <button key={s.source} data-testid={`source-drill-${s.source}`} onClick={() => navigate(`/candidates?source=${s.source}`)}
              className="w-full flex items-center justify-between border-b last:border-0 py-2 text-sm hover:text-[#E05A47] transition-colors duration-100">
              <span className="capitalize">{s.source}</span>
              <span className="font-bold text-[#0A251D]">{s.count}</span>
            </button>
          ))}
          <div className="text-xs uppercase tracking-widest text-muted-foreground mt-5 mb-3 font-bold">Requisitions by Entity</div>
          {data.requisitions_by_entity.filter((s) => s.entity_id).map((s) => (
            <button key={s.entity} data-testid={`entity-drill-${s.entity_id}`} onClick={() => navigate(`/requisitions?entity=${s.entity_id}`)}
              className="w-full flex items-center justify-between border-b last:border-0 py-2 text-sm hover:text-[#E05A47] transition-colors duration-100">
              <span className="text-left">{s.entity}</span>
              <span className="font-bold">{s.count}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="border bg-white rounded-xl p-5 mt-4" data-testid="recent-activity-panel">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Recent Activity (Audit)</div>
          <Link to="/audit" data-testid="view-all-audit-link" className="text-xs font-bold text-[#E05A47] hover:underline">View full audit log →</Link>
        </div>
        {data.recent_activity.map((a) => {
          const to = entityLink(a.entity_type, a.entity_id);
          const Row = (
            <>
              <span className="text-[10px] uppercase tracking-wider bg-secondary px-2 py-0.5 rounded mt-0.5">{a.action}</span>
              <span className="flex-1 text-left">{a.details}</span>
              <span className="text-xs text-muted-foreground whitespace-nowrap">{a.actor_name}</span>
            </>
          );
          return to ? (
            <button key={a.id} data-testid={`activity-drill-${a.id}`} onClick={() => navigate(to)}
              className="w-full flex items-start gap-3 border-b last:border-0 py-2 text-sm hover:text-[#E05A47] transition-colors duration-100">{Row}</button>
          ) : (
            <div key={a.id} className="flex items-start gap-3 border-b last:border-0 py-2 text-sm">{Row}</div>
          );
        })}
      </div>
    </div>
  );
}
